// client/src/pages/Audit/index.jsx
/**
 * Page /audit — Journal d'audit d'activité des guichetiers (web-client serveur).
 * Lecture seule, réservée admin/manager (route protégée + permission audit.view).
 * Filtres (catégorie/action/dates) + table paginée + export CSV.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import auditService from '../../services/auditService';

const CATEGORIES = [
  ['', 'Toutes catégories'],
  ['ticket', 'Tickets'],
  ['counter', 'Guichets'],
  ['session', 'Présence (login/logout)'],
];
const ACTION_LABELS = {
  created: 'Créé', called: 'Appelé', recalled: 'Rappelé', paused: 'Pause',
  resumed: 'Repris', completed: 'Terminé', cancelled: 'Annulé',
  status: 'Statut guichet', login: 'Connexion', logout: 'Déconnexion',
};
const LIMIT = 25;

function AuditPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ category: '', action: '', startDate: '', endDate: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildParams = useCallback(() => {
    const p = { page, limit: LIMIT };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters, page]);

  const load = useCallback(() => {
    setBusy(true);
    auditService.list(buildParams())
      .then((d) => setData(d))
      .catch((e) => {
        setData({ entries: [], total: 0 });
        toast.error(e?.response?.data?.error || e?.message || 'Erreur de chargement');
      })
      .finally(() => setBusy(false));
  }, [buildParams]);

  useEffect(() => { load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => { if (page !== 1) setPage(1); else load(); };
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportCsv = async () => {
    try {
      const blob = await auditService.exportCsv(buildParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'ticketflow-audit.csv';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Export impossible');
    }
  };

  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Audit — activité des guichetiers</h1>
          <button
            onClick={() => navigate('/distributor')}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >← Retour</button>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
          <label className="flex flex-col text-xs text-gray-500">
            Catégorie
            <select className="mt-1 border rounded px-2 py-1 text-sm text-gray-800"
              value={filters.category} onChange={(e) => setF('category', e.target.value)}>
              {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            Action
            <input className="mt-1 border rounded px-2 py-1 text-sm" placeholder="ex. called"
              value={filters.action} onChange={(e) => setF('action', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            Du
            <input type="date" className="mt-1 border rounded px-2 py-1 text-sm"
              value={filters.startDate} onChange={(e) => setF('startDate', e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-gray-500">
            Au
            <input type="date" className="mt-1 border rounded px-2 py-1 text-sm"
              value={filters.endDate} onChange={(e) => setF('endDate', e.target.value)} />
          </label>
          <button onClick={applyFilters} disabled={busy}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50">
            Filtrer
          </button>
          <button onClick={exportCsv} disabled={busy || total === 0}
            className="border border-gray-300 text-gray-700 text-sm px-4 py-1.5 rounded hover:bg-gray-50 disabled:opacity-50">
            ⬇ Export CSV
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          {data === null ? (
            <p className="p-6 text-gray-400">Chargement…</p>
          ) : total === 0 ? (
            <p className="p-6 text-gray-400">Aucune activité enregistrée.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {['Date/heure', 'Action', 'Ticket', 'Service', 'Guichet', 'Agent', 'État'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString('fr-FR') : '—'}</td>
                    <td className="px-3 py-2">{ACTION_LABELS[e.action] || e.action}</td>
                    <td className="px-3 py-2">{e.ticketNumber ?? '—'}</td>
                    <td className="px-3 py-2">{e.serviceName ?? e.serviceType ?? '—'}</td>
                    <td className="px-3 py-2">{e.counterName ?? (e.counterId != null ? `Guichet ${e.counterId}` : '—')}</td>
                    <td className="px-3 py-2">{e.agentName ?? e.agentId ?? '—'}</td>
                    <td className="px-3 py-2">{e.status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-600">
            <button onClick={() => setPage((n) => Math.max(1, n - 1))} disabled={busy || page <= 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-40">‹ Précédent</button>
            <span>page {page} / {pages} — {total} entrées</span>
            <button onClick={() => setPage((n) => Math.min(pages, n + 1))} disabled={busy || page >= pages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-40">Suivant ›</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AuditPage;
